import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Eye, EyeOff, History, Plus, Search, RotateCcw, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api from "../../lib/api";
import { useContent } from "../../context/ContentContext";

const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-colors whitespace-nowrap";

function HistoryPanel({ onRestored }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/cms/log", { params: { limit: 80 } }).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const restore = async (v) => {
    try {
      const { data } = await api.post(`/cms/versions/${v.id}/restore`);
      toast.success("Restored");
      onRestored?.(v, data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
  };
  const fmt = (x) => (x === undefined || x === null ? "—" : typeof x === "object" ? JSON.stringify(x).slice(0, 120) : String(x).slice(0, 120));
  return (
    <div className="space-y-3" data-testid="history-panel">
      {rows.length === 0 && <p className="text-sm text-neutral-500">No changes yet.</p>}
      {rows.map((v) => (
        <div key={v.id} className="rounded-xl border p-3 text-sm" data-testid={`history-row-${v.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-bold">{v.label} <span className="text-[10px] uppercase font-bold text-neutral-400 ml-1">{v.entity_type} · {v.action}</span></p>
              <p className="text-xs text-neutral-500">{new Date(v.at).toLocaleString()} · {v.by}</p>
            </div>
            {v.action !== "restore" && (
              <button type="button" data-testid={`restore-${v.id}`} onClick={() => restore(v)} className={`${btn} bg-neutral-100 hover:bg-neutral-200`}><RotateCcw size={12} /> Restore</button>
            )}
          </div>
          <ul className="mt-2 space-y-1">
            {(v.fields || []).map((f) => (
              <li key={f} className="text-xs"><span className="font-semibold">{f}:</span> <s className="text-neutral-400">{fmt(v.before?.[f])}</s> → <span className="text-green-700">{fmt(v.after?.[f])}</span></li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function HiddenPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/cms/hidden").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const unhide = async (type, id, fields) => {
    try { await api.patch(`/cms/${type}/${id}`, { fields }); toast.success("Visible again"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not unhide"); }
  };
  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;
  const Section = ({ title, items, type, fields, nameKey = "name", empty }) => (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">{title}</p>
      {items.length === 0 ? <p className="text-xs text-neutral-400 mb-3">{empty}</p> : items.map((it) => (
        <div key={it.id} className="flex items-center justify-between gap-2 py-1.5 border-b text-sm" data-testid={`hidden-${type}-${it.id}`}>
          <span className="truncate">{it[nameKey]}</span>
          <button type="button" data-testid={`unhide-${type}-${it.id}`} onClick={() => unhide(type, it.id, fields)} className={`${btn} bg-green-600 text-white hover:bg-green-700`}><Eye size={12} /> {fields.in_stock !== undefined ? "Mark available" : "Unhide"}</button>
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-5" data-testid="hidden-panel">
      <Section title="Hidden products" items={data.products} type="product" fields={{ active: true }} empty="None hidden" />
      <Section title="Unavailable (sold out) products" items={data.unavailable} type="product" fields={{ in_stock: true }} empty="Everything is available" />
      <Section title="Hidden categories" items={data.categories} type="category" fields={{ hidden: false }} empty="None hidden" />
      <Section title="Hidden brands" items={data.businesses} type="business" fields={{ active: true }} empty="None hidden" />
      <Section title="Hidden pages" items={data.pages} type="page" fields={{ hidden: false }} nameKey="title" empty="None hidden" />
    </div>
  );
}

function AddDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState("page");
  const [bizs, setBizs] = useState([]);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ business_id: "", category_id: "", title: "", name: "", description: "", intro: "", show_in_nav: false, show_in_footer: true });
  useEffect(() => { if (open) api.get("/businesses/all").then(({ data }) => { setBizs(data); setF((x) => ({ ...x, business_id: x.business_id || data[0]?.id || "" })); }).catch(() => {}); }, [open]);
  useEffect(() => { if (f.business_id) api.get(`/businesses/${f.business_id}/categories`, { params: { include_hidden: true } }).then(({ data }) => setCats(data)).catch(() => setCats([])); }, [f.business_id]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const inputCls = "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm";
  const submit = async () => {
    try {
      if (kind === "page") {
        const { data } = await api.post("/cms/page", { title: f.title, intro: f.intro, show_in_nav: f.show_in_nav, show_in_footer: f.show_in_footer });
        toast.success(`Page created at /p/${data.slug}`); onOpenChange(false); navigate(`/p/${data.slug}`);
      } else if (kind === "category") {
        await api.post("/cms/category", { business_id: f.business_id, name: f.name });
        toast.success("Category added"); onOpenChange(false); window.location.reload();
      } else {
        await api.post("/cms/product", { business_id: f.business_id, category_id: f.category_id || null, name: f.name, description: f.description });
        toast.success("Item added — shows 'Price TBD' until the POS sends its price"); onOpenChange(false); window.location.reload();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[90]" data-testid="add-dialog">
        <DialogHeader><DialogTitle className="font-display">Add new</DialogTitle></DialogHeader>
        <div className="flex gap-1 mb-2">
          {["page", "category", "product"].map((k) => (
            <button key={k} type="button" data-testid={`add-kind-${k}`} onClick={() => setKind(k)} className={`${btn} ${kind === k ? "bg-black text-white" : "bg-neutral-100"}`}>{k}</button>
          ))}
        </div>
        <div className="space-y-3">
          {kind === "page" ? (
            <>
              <input data-testid="add-title" className={inputCls} placeholder="Page title (e.g. Private Events)" value={f.title} onChange={(e) => set("title", e.target.value)} />
              <textarea data-testid="add-intro" className={inputCls} rows={3} placeholder="Short intro" value={f.intro} onChange={(e) => set("intro", e.target.value)} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.show_in_nav} onChange={(e) => set("show_in_nav", e.target.checked)} /> Show in top navigation</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.show_in_footer} onChange={(e) => set("show_in_footer", e.target.checked)} /> Show in footer</label>
            </>
          ) : (
            <>
              <select data-testid="add-business" className={inputCls} value={f.business_id} onChange={(e) => set("business_id", e.target.value)}>
                {bizs.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <input data-testid="add-name" className={inputCls} placeholder={kind === "category" ? "Category name" : "Item name (exactly as in the POS)"} value={f.name} onChange={(e) => set("name", e.target.value)} />
              {kind === "product" && (
                <>
                  <select data-testid="add-category" className={inputCls} value={f.category_id} onChange={(e) => set("category_id", e.target.value)}>
                    <option value="">— category —</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <textarea data-testid="add-description" className={inputCls} rows={3} placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} />
                  <p className="text-xs text-neutral-500">Price comes from the POS. Until it arrives the item shows "Price TBD" and can't be ordered.</p>
                </>
              )}
            </>
          )}
          <div className="flex justify-end"><button type="button" data-testid="add-submit" onClick={submit} className={`${btn} bg-black text-white`}><Plus size={12} /> Create</button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SeoDialog({ open, onOpenChange }) {
  const { pathname } = useLocation();
  const { get, save } = useContent();
  const key = `seo:${pathname.replace(/\/+$/, "") || "/"}`;
  const cur = get(key, null) || {};
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  useEffect(() => { if (open) { setTitle(cur.title || document.title); setDesc(cur.description || document.querySelector('meta[name="description"]')?.content || ""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    try { await save(key, { title, description: desc }, { type: "seo", label: `SEO ${pathname}` }); toast.success("SEO saved — visitors see it now; Google after you Publish"); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const inputCls = "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[90]" data-testid="seo-dialog">
        <DialogHeader><DialogTitle className="font-display">SEO for {pathname}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-bold uppercase text-neutral-500">Title <span className="font-normal normal-case">({title.length}/60)</span></label><input data-testid="seo-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><label className="text-xs font-bold uppercase text-neutral-500">Description <span className="font-normal normal-case">({desc.length}/160)</span></label><textarea data-testid="seo-description" rows={3} className={inputCls} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="flex justify-end"><button type="button" data-testid="seo-save" onClick={submit} className={`${btn} bg-black text-white`}>Save</button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditBar() {
  const { isOwner, editMode, setEditMode } = useContent();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [seoOpen, setSeoOpen] = useState(false);
  if (!isOwner) return null;
  const undoLast = async () => {
    try { const { data } = await api.post("/cms/undo-last"); toast.success(`Undid: ${data.undone?.label || "last change"}`); setTimeout(() => window.location.reload(), 600); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to undo"); }
  };
  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-1.5 bg-[#0F0F0F]/95 backdrop-blur text-white rounded-full pl-2 pr-2 py-1.5 shadow-2xl border border-white/10 max-w-[96vw] overflow-x-auto" data-testid="owner-edit-bar">
        <button type="button" data-testid="edit-mode-toggle" onClick={() => setEditMode(!editMode)}
          className={`${btn} ${editMode ? "bg-[#F0B429] text-black" : "bg-white/10 hover:bg-white/20"}`}>
          {editMode ? <EyeOff size={13} /> : <Pencil size={13} />} {editMode ? "Editing — click to stop" : "Edit site"}
        </button>
        {editMode && (
          <>
            <button type="button" data-testid="edit-bar-seo" onClick={() => setSeoOpen(true)} className={`${btn} bg-white/10 hover:bg-white/20`}><Search size={13} /> Page SEO</button>
            <button type="button" data-testid="edit-bar-add" onClick={() => setAddOpen(true)} className={`${btn} bg-white/10 hover:bg-white/20`}><Plus size={13} /> Add</button>
            <button type="button" data-testid="edit-bar-hidden" onClick={() => setPanel("hidden")} className={`${btn} bg-white/10 hover:bg-white/20`}><EyeOff size={13} /> Hidden items</button>
            <button type="button" data-testid="edit-bar-history" onClick={() => setPanel("history")} className={`${btn} bg-white/10 hover:bg-white/20`}><History size={13} /> History</button>
            <button type="button" data-testid="edit-bar-undo" onClick={undoLast} className={`${btn} bg-white/10 hover:bg-white/20`}><RotateCcw size={13} /> Undo last</button>
          </>
        )}
        <button type="button" data-testid="edit-bar-dashboard" onClick={() => navigate("/dashboard")} className={`${btn} bg-white/10 hover:bg-white/20`}><LayoutDashboard size={13} /> Back Office</button>
      </div>
      <Sheet open={!!panel} onOpenChange={(o) => !o && setPanel(null)}>
        <SheetContent side="right" className="w-[min(96vw,30rem)] overflow-y-auto z-[85]">
          <SheetHeader><SheetTitle className="font-display">{panel === "hidden" ? "Hidden items" : "Change history"}</SheetTitle></SheetHeader>
          <div className="mt-4">
            {panel === "hidden" && <HiddenPanel onChanged={() => {}} />}
            {panel === "history" && <HistoryPanel onRestored={() => setTimeout(() => window.location.reload(), 500)} />}
          </div>
        </SheetContent>
      </Sheet>
      <AddDialog open={addOpen} onOpenChange={setAddOpen} />
      <SeoDialog open={seoOpen} onOpenChange={setSeoOpen} />
    </>
  );
}
